import { useEffect, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { AuthLayout } from '../layouts/AuthLayout'
import { Card } from '../components/ui/Card'

/** Exchange a one-time support launch token and enter the merchant dashboard. */
export default function SupportAccessPage() {
  const [params] = useSearchParams()
  const token = params.get('token')?.trim() ?? ''
  const { startSupportAccess, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(Boolean(token))

  useEffect(() => {
    if (!token) return
    let cancelled = false
    ;(async () => {
      setBusy(true)
      setError(null)
      try {
        await startSupportAccess(token)
        if (!cancelled) {
          navigate('/', { replace: true })
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Support access link is invalid or expired')
        }
      } finally {
        if (!cancelled) setBusy(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token, startSupportAccess, navigate])

  if (isAuthenticated && !token) {
    return <Navigate to="/" replace />
  }

  return (
    <AuthLayout>
      <Card className="mx-auto w-full max-w-md p-8 text-center">
        {!token ? (
          <>
            <h1 className="text-lg font-semibold">Invalid support link</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              This link is missing a token. Request a new session from the platform admin console.
            </p>
          </>
        ) : busy ? (
          <>
            <h1 className="text-lg font-semibold">Opening support session…</h1>
            <p className="mt-2 text-sm text-muted-foreground">Verifying your access link.</p>
          </>
        ) : error ? (
          <>
            <h1 className="text-lg font-semibold">Could not start support session</h1>
            <p className="mt-2 text-sm text-destructive" role="alert">
              {error}
            </p>
          </>
        ) : null}
      </Card>
    </AuthLayout>
  )
}
